import { DEFAULT_ERROR_MESSAGE } from '../../helpers/Constants'

/**
 * FastAPI's 422 `detail` as one readable line: `[{loc, msg}]` becomes
 * "comms: value is not a valid dict", a string detail is used as is.
 */
const validationDetail = (detail) => {
    if (typeof detail === 'string') return detail
    if (!Array.isArray(detail) || !detail.length) return ''
    return detail
        .map((d) => {
            const field = (d?.loc ?? []).filter((p) => p !== 'body').join('.')
            return field ? `${field}: ${d?.msg}` : d?.msg
        })
        .filter(Boolean)
        .join('; ')
}

// A page from the gateway or a proxy (nginx's "503 Service Temporarily
// Unavailable", a CloudFront error) rather than the API's own answer.
const HTML_RE = /^\s*<(!doctype|html|head|body)\b/i

/** What to say for an HTML error page instead of printing its markup. */
const gatewayMessage = (status) =>
    status
        ? `The server is not answering right now (${status}). Try again in a minute.`
        : 'The server is not answering right now. Try again in a minute.'

/**
 * The message to show for a failed request, most specific first:
 *   - an HTML page from the gateway (a 502, 503 or 504 from nginx), as a
 *     plain sentence with its status, never its markup;
 *   - a bare string body (admin_auth_fastapi's 403 "You don't have
 *     permissions to access this endpoint, contact administrator");
 *   - `{ error }`, which most of the older routes send;
 *   - `{ status: 'FAILURE', message }`, the newer v2 routes' ErrorResponse
 *     ("template body and dlt_template_id are not found");
 *   - FastAPI's validation `detail` (422, or `{ detail: 'Not Found' }`);
 *   - a 403 hint, then the generic default.
 * A `message` on a body that is not a FAILURE response is ignored: success
 * payloads carry `message` too.
 */
export const errorMessage = (response) => {
    const data = response?.data
    if (typeof data === 'string' && HTML_RE.test(data))
        return gatewayMessage(response?.status)
    if (typeof data === 'string' && data.trim()) return data
    if (data?.error && typeof data.error === 'string') return data.error
    if (data?.status === 'FAILURE' && data?.message) return data.message
    const detail = validationDetail(data?.detail)
    if (detail) return detail
    if (response?.status === 403)
        return "You don't have access to this. Ask an admin for the comms role."
    return DEFAULT_ERROR_MESSAGE
}
